import { FC, useCallback, useMemo, useState } from 'react';
import { addAddressPadding } from 'starknet';
import ModalWrapper from '../../ModalWrapper';
import BaseInput from '../../BaseInput';
import { CurrencyInputToken } from '../CurrencyInputTypes';
import TokenSelectorItem from './TokenSelectorItem';
import Icon from '../../Icon';
import Typography from '../../Typography';
import Logo from '../../Logo';
import { ButtonWrapper, LogoType } from '@tempusfinance/common-ui';

import './TokenSelector.scss';
import { Decimal } from '../../../../datastructures';
import { useTokenPriceResolver } from '../../../../utils/useTokenPriceResolver';
import { FAVOURITE_TOKENS } from '../../../../constants';

interface TokenSelectorProps {
  open: boolean;
  tokens: CurrencyInputToken[];
  tokenSearch?: boolean;
  collapseTokenList?: boolean;
  onTokenSelected: (token: CurrencyInputToken) => void;
  onClose: () => void;
}

const TokenSelector: FC<TokenSelectorProps> = ({
  onClose,
  onTokenSelected,
  open,
  tokenSearch = true,
  collapseTokenList = false,
  tokens,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchFocus, setSearchFocus] = useState<boolean>(false);

  const resolvePrice = useTokenPriceResolver();

  // Enrich all tokens with resolved prices so both sorting and child
  // components can use token.price consistently.
  const tokensWithPrices = useMemo(
    () =>
      tokens.map(token => {
        if (token.price) return token;

        const resolved = resolvePrice(token.address, token.symbol);
        if (resolved) {
          return { ...token, price: resolved };
        }

        return token;
      }),
    [tokens, resolvePrice],
  );

  const favouriteTokens = useMemo(
    () =>
      FAVOURITE_TOKENS.map(token => tokensWithPrices.find(t => t.symbol === token)).filter(token =>
        Boolean(token),
      ) as CurrencyInputToken[],
    [tokensWithPrices],
  );

  const sortedTokens = useMemo(() => {
    return [...tokensWithPrices].sort((a, b) => {
      if (!a.balance || !b.balance) {
        return 0;
      }

      const priceA = a.price ?? new Decimal(1n, Decimal.PRECISION);
      const priceB = b.price ?? new Decimal(1n, Decimal.PRECISION);

      const valueA = a.balance.mul(priceA);
      const valueB = b.balance.mul(priceB);

      return valueA.gt(valueB) ? -1 : 1;
    });
  }, [tokensWithPrices]);

  const filteredTokens = useMemo(
    () =>
      sortedTokens.filter(token => {
        try {
          if (
            searchTerm &&
            !token.symbol.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !addAddressPadding(token.address.toLowerCase()).includes(
              searchTerm.startsWith('0x') ? addAddressPadding(searchTerm) : searchTerm.toLowerCase(),
            )
          ) {
            return false;
          }

          return true;
        } catch (e) {
          return false;
        }
      }),
    [sortedTokens, searchTerm],
  );

  const onSearchTermChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const onFocusSearch = useCallback(() => {
    setSearchFocus(true);
  }, []);
  const onBlurSearch = useCallback(() => {
    setSearchFocus(false);
  }, []);

  return (
    <ModalWrapper onClose={onClose} open={open}>
      <div className="nostra__tokenSelector">
        {tokenSearch && (
          <div className={`nostra__tokenSelector__input ${searchFocus ? 'nostra__tokenSelector__inputFocus' : ''}`}>
            <Icon variant="search" size={16} color="--icon-supporting" />
            <Typography variant="input-body-lg">
              <BaseInput
                value={searchTerm}
                onChange={onSearchTermChange}
                onFocus={onFocusSearch}
                onBlur={onBlurSearch}
                placeholder="Search asset name or address"
              />
            </Typography>
          </div>
        )}
        <div className="nostra__tokenSelector__favouriteTokens">
          {favouriteTokens.map(token => (
            <ButtonWrapper
              key={token.symbol}
              className="nostra__tokenSelector__favouriteToken"
              onClick={() => onTokenSelected(token)}
            >
              <Logo type={`token-${token.symbol}` as LogoType} size="small" />
              <Typography variant="button-sm" weight="semi-bold">
                {token.symbol}
              </Typography>
            </ButtonWrapper>
          ))}
        </div>
        {tokenSearch && <div className="nostra__tokenSelector__divider" />}
        <div className="nostra__tokenSelector__tokenList" data-collapse={collapseTokenList}>
          {filteredTokens.map(token => (
            <TokenSelectorItem token={token} onTokenSelected={onTokenSelected} key={token.symbol} />
          ))}
        </div>
      </div>
    </ModalWrapper>
  );
};
export default TokenSelector;
